import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useProfile } from "@moonscape/shared";
import { XMarkIcon, ChevronUpIcon, ChevronDownIcon } from "@heroicons/react/24/outline";
import { cn } from "@/utils";
import { Callout, DHead, Lab } from "@/components/ms";

interface TaxTip {
  title: string;
  content: string;
  category: string;
}

export const TaxTipsWidget = () => {
  const { profile } = useProfile();
  const taxJurisdiction = profile?.taxJurisdiction;
  const [currentTipIndex, setCurrentTipIndex] = useState(0);
  const [isDismissed, setIsDismissed] = useState(() =>
    localStorage.getItem('taxTipsDismissed') === 'true'
  );
  const [isCollapsed, setIsCollapsed] = useState(() =>
    localStorage.getItem('taxTipsCollapsed') === 'true'
  );

  const handleDismiss = () => {
    setIsDismissed(true);
    localStorage.setItem('taxTipsDismissed', 'true');
  };

  const toggleCollapse = () => {
    const newState = !isCollapsed;
    setIsCollapsed(newState);
    localStorage.setItem('taxTipsCollapsed', String(newState));
  };

  // Tax tips by jurisdiction
  const getTaxTips = (): TaxTip[] => {
    switch (taxJurisdiction) {
      case "United Kingdom":
      case "UK":
        return [
          {
            title: "Use your CGT allowance",
            content:
              "Your CGT allowance is £3,000 (2024/25 to 2026/27), cut in two steps from £12,300 (to 2022/23) via £6,000 (2023/24). Realize gains up to this amount tax-free.",
            category: "Allowances",
          },
          {
            title: "Same day and 30-day rules",
            content:
              "HMRC has specific matching rules. Same-day purchases are matched first, then 30-day rule applies.",
            category: "HMRC rules",
          },
          {
            title: "Pool your holdings",
            content:
              "After same-day and 30-day matching, crypto is pooled using the average cost basis method (Section 104 holding).",
            category: "Cost basis",
          },
          {
            title: "Report by 31 January",
            content:
              "Report crypto gains via Self Assessment if they exceed the £3,000 allowance or total proceeds exceed £50,000. There's no 30-day deadline for crypto — that rule is for residential property only.",
            category: "Reporting",
          },
        ];
      case "United States":
      case "US":
        return [
          {
            title: "Hold for long-term gains",
            content:
              "Hold crypto for over 1 year to qualify for long-term capital gains rates (0%, 15%, or 20%) instead of ordinary income tax rates.",
            category: "Capital gains",
          },
          {
            title: "Tax-loss harvesting",
            content:
              "Sell losing investments to offset gains. You can deduct up to $3,000 in net losses against ordinary income per year.",
            category: "Tax strategy",
          },
          {
            title: "Track every transaction",
            content:
              "The IRS requires reporting all crypto transactions. Keep detailed records of dates, amounts, and fair market values.",
            category: "Record keeping",
          },
          {
            title: "Beware of wash sale rules",
            content:
              "While wash sale rules don't officially apply to crypto yet, consider the 30-day rule to avoid potential issues.",
            category: "Compliance",
          },
        ];
      case "Canada":
      case "CA":
        return [
          {
            title: "50% inclusion rate",
            content:
              "In Canada, only 50% of your capital gains are taxable. The other 50% is tax-free.",
            category: "Capital gains",
          },
          {
            title: "Business vs investment income",
            content:
              "CRA may treat frequent crypto trading as business income (100% taxable) rather than capital gains. Keep activity reasonable.",
            category: "Classification",
          },
        ];
      case "Australia":
      case "AU":
        return [
          {
            title: "50% CGT discount",
            content:
              "Hold crypto for over 12 months and you'll pay tax on only 50% of the capital gain (for individuals). A 2026 budget proposal would end this from 1 July 2027.",
            category: "CGT discount",
          },
        ];
      case "Germany":
      case "DE":
        return [
          {
            title: "One year tax-free rule",
            content:
              "Under current German rules, crypto held for more than one year is tax-free when sold. A 2026 draft bill would end this from January 2027 for coins acquired after 31 Dec 2026.",
            category: "Tax-free gains",
          },
        ];
      case "France":
      case "FR":
        return [
          {
            title: "Flat tax rate",
            content:
              "France applies a flat 31.4% tax rate (12.8% income tax + 18.6% social contributions) on crypto gains, up from 30% before 2026.",
            category: "Tax rates",
          },
        ];
      default:
        return [
          {
            title: "Keep detailed records",
            content:
              "Maintain comprehensive records of all crypto transactions including dates, amounts, and market values.",
            category: "Record keeping",
          },
          {
            title: "Understand tax events",
            content:
              "Know what triggers taxes: selling crypto, trading one crypto for another, using crypto for purchases, and receiving crypto as income.",
            category: "Tax events",
          },
        ];
    }
  };

  const taxTips = getTaxTips();
  const currentTip = taxTips[currentTipIndex];

  // Auto-rotation effect
  useEffect(() => {
    if (taxTips.length <= 1) return;

    const timer = setInterval(() => {
      setCurrentTipIndex((prevIndex) => (prevIndex + 1) % taxTips.length);
    }, 8000); // 8 seconds like iOS

    return () => clearInterval(timer);
  }, [taxTips.length]);

  // Early return AFTER all hooks
  if (isDismissed) {
    return null;
  }

  return (
    <section className="flex flex-col">
      <DHead
        label={taxJurisdiction ? `Tax tips · ${taxJurisdiction}` : "Tax tips"}
        right={
          <span className="flex items-center gap-1">
            <button
              type="button"
              onClick={toggleCollapse}
              className="grid h-8 w-8 place-items-center text-ms-dim transition-colors hover:text-ms-ink"
              title={isCollapsed ? "Expand" : "Collapse"}
            >
              {isCollapsed ? (
                <ChevronDownIcon className="h-4 w-4" />
              ) : (
                <ChevronUpIcon className="h-4 w-4" />
              )}
            </button>
            <button
              type="button"
              onClick={handleDismiss}
              className="grid h-8 w-8 place-items-center text-ms-dim transition-colors hover:text-ms-ink"
              title="Dismiss"
            >
              <XMarkIcon className="h-4 w-4" />
            </button>
          </span>
        }
      />

      {!isCollapsed && (
        <>
          <Callout className="mt-3.5">
            <Lab size={9} className="block text-ms-dim">
              {currentTip.category}
            </Lab>
            <b className="mt-1.5 block text-[13.5px]">{currentTip.title}</b>
            <span className="mt-1.5 block">{currentTip.content}</span>
            <Link to="/tax/cgt" className="mt-2.5 inline-block">
              Learn more →
            </Link>
          </Callout>

          {taxTips.length > 1 && (
            <div className="mt-3 flex gap-1.5">
              {taxTips.map((_, index) => (
                <button
                  key={index}
                  type="button"
                  onClick={() => setCurrentTipIndex(index)}
                  className={cn(
                    "h-[3px] transition-all",
                    index === currentTipIndex
                      ? "w-6 bg-ms-rule"
                      : "w-2.5 bg-ms-line2 hover:bg-ms-dim"
                  )}
                  aria-label={`Go to tip ${index + 1}`}
                />
              ))}
            </div>
          )}
        </>
      )}
    </section>
  );
};
